import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { usersQueryOptions, type AdminUserRow } from "@/lib/users.queries";
import { EMAIL_ERROR, isValidEmail } from "@/lib/validate";
import {
  deleteUser,
  inviteUser,
  resendInvitation,
  sendPasswordReset,
  setUserAdminRole,
} from "@/lib/users.functions";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Mail, MoreVertical, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { FormDialog, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersAdmin,
});

// Cognito UserStatus -> label + colour. Disabled wins over any status.
const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  CONFIRMED: {
    label: "Active",
    className: "border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  },
  FORCE_CHANGE_PASSWORD: {
    label: "Invited",
    className: "border-transparent bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
  },
  RESET_REQUIRED: {
    label: "Reset pending",
    className: "border-transparent bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  },
};

function StatusBadge({ user }: { user: AdminUserRow }) {
  if (user.enabled === false) {
    return (
      <Badge variant="outline" className="border-transparent bg-muted text-muted-foreground">
        Disabled
      </Badge>
    );
  }
  if (!user.status) return <span className="text-muted-foreground">—</span>;
  const known = STATUS_BADGES[user.status];
  if (known) return <Badge variant="outline" className={known.className}>{known.label}</Badge>;
  return <Badge variant="outline" className="font-mono font-normal">{user.status}</Badge>;
}

type RowAction = { kind: "reset" | "resend" | "delete"; user: AdminUserRow };

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

function UsersAdmin() {
  const { data: rows = [], isLoading } = useQuery(usersQueryOptions());
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const setRole = useServerFn(setUserAdminRole);
  const del = useServerFn(deleteUser);
  const invite = useServerFn(inviteUser);
  const sendReset = useServerFn(sendPasswordReset);
  const resendInvite = useServerFn(resendInvitation);

  // One confirmation at a time: the row action being confirmed, or null.
  const [action, setAction] = useState<RowAction | null>(null);
  // Keeps the dialog's text in place while its close animation runs.
  const lastAction = useRef<RowAction | null>(null);
  if (action) lastAction.current = action;
  const shownAction = action ?? lastAction.current;
  // Kebab trigger to hand focus back to when the confirmation closes.
  const returnFocusTo = useRef<HTMLElement | null>(null);

  const isSelf = (u: AdminUserRow) =>
    !!me &&
    ((!!me.sub && u.id === me.sub) ||
      (!!me.email && !!u.email && u.email.toLowerCase() === me.email.toLowerCase()));

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [inviteAdmin, setInviteAdmin] = useState(false);
  const inviteDirty = useDirty({ inviteEmail, inviteAdmin }, inviteOpen);

  const roleMut = useMutation({
    mutationFn: (vars: { userId: string; isAdmin: boolean }) => setRole({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_users"] });
      toast.success("Role updated");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const delMut = useMutation({
    mutationFn: (u: AdminUserRow) => del({ data: { userId: u.id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_users"] });
      toast.success("User removed");
    },
    onError: (e) => toast.error(errorText(e, "Delete failed")),
    onSettled: () => setAction(null),
  });

  const resetMut = useMutation({
    mutationFn: (u: AdminUserRow) => sendReset({ data: { userId: u.id } }),
    onSuccess: (res, u) => {
      qc.invalidateQueries({ queryKey: ["admin_users"] });
      toast.success(`Password reset email sent to ${res?.email ?? u.email ?? "the user"}.`);
    },
    onError: (e) => toast.error(errorText(e, "Could not send the reset email.")),
    onSettled: () => setAction(null),
  });

  const resendMut = useMutation({
    mutationFn: (u: AdminUserRow) => resendInvite({ data: { userId: u.id } }),
    onSuccess: (res, u) => {
      qc.invalidateQueries({ queryKey: ["admin_users"] });
      toast.success(`Invitation resent to ${res?.email ?? u.email ?? "the user"}.`);
    },
    onError: (e) => toast.error(errorText(e, "Could not resend the invitation.")),
    onSettled: () => setAction(null),
  });

  const actionPending = delMut.isPending || resetMut.isPending || resendMut.isPending;

  const openAction = (kind: RowAction["kind"], user: AdminUserRow) => {
    returnFocusTo.current = document.querySelector<HTMLElement>(
      `[data-user-actions="${CSS.escape(user.id)}"]`,
    );
    setAction({ kind, user });
  };

  const inviteMut = useMutation({
    mutationFn: () => invite({ data: { email: inviteEmail.trim(), makeAdmin: inviteAdmin } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_users"] });
      setInviteOpen(false);
      setInviteEmail("");
      setInviteAdmin(false);
      toast.success("Invitation sent");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Invite failed"),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Users & Roles</CardTitle>
          <CardDescription>Manage authenticated users and admin access.</CardDescription>
        </div>
        <Button size="sm" onClick={() => setInviteOpen(true)}><Plus className="mr-1 h-4 w-4" /> Invite user</Button>
        <FormDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          size="md"
          icon={UserPlus}
          title="Invite a user"
          context="Sends a sign-up invitation by email"
          dirty={inviteDirty}
          busy={inviteMut.isPending}
          asForm
          noValidate
          onSubmit={() => {
            if (!isValidEmail(inviteEmail)) {
              setInviteEmailError(EMAIL_ERROR);
              return;
            }
            inviteMut.mutate();
          }}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <SaveButton dirty={inviteDirty} pending={inviteMut.isPending} label="Send invite" pendingLabel="Sending…" />
            </>
          }
        >
          <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={inviteEmail}
                  aria-invalid={inviteEmailError ? true : undefined}
                  aria-describedby={inviteEmailError ? "invite-email-error" : undefined}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    if (inviteEmailError) setInviteEmailError(null);
                  }}
                />
                {inviteEmailError && (
                  <p id="invite-email-error" className="text-xs text-destructive">
                    {inviteEmailError}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                <Switch id="inv-admin" checked={inviteAdmin} onCheckedChange={setInviteAdmin} />
                <Label htmlFor="inv-admin" className="cursor-pointer">Grant admin role on signup</Label>
              </div>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
              <TableHead className="hidden lg:table-cell">Last sign-in</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-28 text-center">Admin</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!isLoading && rows.map((u: AdminUserRow) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {u.email ?? "—"}
                    {u.is_admin && (
                      <Badge variant="default" className="gap-1">
                        <ShieldCheck className="h-3 w-3" /> Admin
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">
                  {new Date(u.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground">
                  {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell>
                  <StatusBadge user={u} />
                </TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={u.is_admin}
                    disabled={roleMut.isPending}
                    onCheckedChange={(v) => roleMut.mutate({ userId: u.id, isAdmin: v })}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <RowMenu
                    user={u}
                    canManageLogin={!isSelf(u) && u.enabled !== false}
                    onAction={openAction}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No users.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <AlertDialog
        open={!!action}
        onOpenChange={(open) => {
          if (!open && !actionPending) setAction(null);
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(e) => {
            const el = returnFocusTo.current;
            if (el && el.isConnected) {
              e.preventDefault();
              el.focus();
            }
          }}
        >
          {shownAction && (
            <ConfirmBody
              action={shownAction}
              pending={actionPending}
              onConfirm={() => {
                if (!action) return;
                if (action.kind === "reset") resetMut.mutate(action.user);
                else if (action.kind === "resend") resendMut.mutate(action.user);
                else delMut.mutate(action.user);
              }}
            />
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function RowMenu({
  user,
  canManageLogin,
  onAction,
}: {
  user: AdminUserRow;
  /** False on the caller's own row and for disabled users. */
  canManageLogin: boolean;
  onAction: (kind: RowAction["kind"], user: AdminUserRow) => void;
}) {
  const email = user.email ?? "this user";
  const canReset = canManageLogin && (user.status === "CONFIRMED" || user.status === "RESET_REQUIRED");
  const canResend = canManageLogin && user.status === "FORCE_CHANGE_PASSWORD";
  return (
    // modal={false}: a modal menu closing while the AlertDialog opens can leave
    // pointer-events locked on <body> (Radix focus/scroll-lock handoff).
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`More actions for ${email}`}
          data-user-actions={user.id}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {canReset && (
          <DropdownMenuItem className="gap-2" onSelect={() => onAction("reset", user)}>
            <KeyRound className="h-4 w-4" /> Send password reset
          </DropdownMenuItem>
        )}
        {canResend && (
          <DropdownMenuItem className="gap-2" onSelect={() => onAction("resend", user)}>
            <Mail className="h-4 w-4" /> Resend invitation
          </DropdownMenuItem>
        )}
        {(canReset || canResend) && <DropdownMenuSeparator />}
        <DropdownMenuItem
          className="gap-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
          onSelect={() => onAction("delete", user)}
        >
          <Trash2 className="h-4 w-4" /> Delete user
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type ConfirmCopy = {
  title: string;
  body: string;
  cancel: string;
  confirm: string;
  pending: string;
  destructive?: boolean;
};

function confirmCopy(kind: RowAction["kind"], email: string): ConfirmCopy {
  switch (kind) {
    case "reset":
      return {
        title: `Send a password reset to ${email}?`,
        body: `We will email ${email} a code to choose a new password. Their current password stops working right away and they are signed out of every device within the hour. You will not see or set their password.`,
        cancel: "Cancel",
        confirm: "Send reset email",
        pending: "Sending…",
      };
    case "resend":
      return {
        title: `Resend the invitation to ${email}?`,
        body: "We will email a new temporary password, valid for 7 days. The previous one stops working.",
        cancel: "Cancel",
        confirm: "Resend invitation",
        pending: "Sending…",
      };
    case "delete":
      return {
        title: `Delete ${email}?`,
        body: "They lose access to the admin right away. This cannot be undone.",
        cancel: "Keep user",
        confirm: "Delete user",
        pending: "Deleting…",
        destructive: true,
      };
  }
}

function ConfirmBody({
  action,
  pending,
  onConfirm,
}: {
  action: RowAction;
  pending: boolean;
  onConfirm: () => void;
}) {
  const copy = confirmCopy(action.kind, action.user.email ?? "this user");
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>{copy.title}</AlertDialogTitle>
        <AlertDialogDescription>{copy.body}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        {/* Radix AlertDialog focuses Cancel on open, so the safe choice is the default. */}
        <AlertDialogCancel disabled={pending}>{copy.cancel}</AlertDialogCancel>
        {/* A plain Button, not AlertDialogAction: the dialog stays open while the request runs. */}
        <Button
          variant={copy.destructive ? "destructive" : "default"}
          disabled={pending}
          onClick={onConfirm}
        >
          {pending ? copy.pending : copy.confirm}
        </Button>
      </AlertDialogFooter>
    </>
  );
}
